"use client";

import React, { useEffect, useState } from "react";

import { cn } from "@rivval/css";

interface ConnectLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  text?: string;
  subtext?: string;
}

export function ConnectLoader({
  text = "LOADING",
  subtext = "ANALYZE",
  className,
  ...props
}: ConnectLoaderProps) {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const formatTime = (date: Date) => {
    return date.toTimeString().slice(0, 5);
  };

  const formatDate = (date: Date) => {
    return date
      .toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
      })
      .replace("/", " ");
  };

  const futureTime = new Date(currentTime.getTime() + 5 * 60000); // 5 minutes in the future

  return (
    <div
      className={cn(
        "relative flex w-full max-w-2xl flex-col items-center justify-center gap-2",
        className,
      )}
      {...props}
    >
      {/* Decorative Elements */}
      <div className="absolute left-0 h-4 w-4">
        <div className="absolute h-1 w-1 bg-foreground" />
        <div className="absolute bottom-0 h-1 w-1 bg-foreground" />
        <div className="absolute right-0 h-1 w-1 bg-foreground" />
        <div className="absolute bottom-0 right-0 h-1 w-1 bg-foreground" />
      </div>
      <div className="absolute right-0 h-4 w-4">
        <div className="absolute h-1 w-1 bg-foreground" />
        <div className="absolute bottom-0 h-1 w-1 bg-foreground" />
        <div className="absolute right-0 h-1 w-1 bg-foreground" />
        <div className="absolute bottom-0 right-0 h-1 w-1 bg-foreground" />
      </div>

      {/* Main Text */}
      <div className="font-mono text-sm tracking-[0.2em] text-foreground">
        {text}
      </div>

      {/* Loading Bar Container */}
      <div className="relative h-1 w-full bg-neutral-900">
        <div className="absolute left-0 flex h-3 -translate-y-1 items-center">
          <div className="h-full w-px bg-foreground" />
          <div className="h-1 w-1 bg-foreground" />
        </div>
        <div className="absolute right-0 flex h-3 -translate-y-1 items-center">
          <div className="h-1 w-1 bg-foreground" />
          <div className="h-full w-px bg-foreground" />
        </div>

        {/* Animated Loading Bar */}
        <div className="relative h-full w-full overflow-hidden">
          <div className="loading-bar absolute inset-0" />
        </div>
      </div>

      {/* Subtext */}
      <div className="font-mono text-[0.65rem] tracking-[0.2em] text-foreground">
        {subtext}
      </div>

      {/* Time and Date Markers */}
      <div className="absolute -top-6 flex w-full justify-between font-mono text-xs text-foreground">
        <span>
          {formatTime(currentTime)} {formatDate(currentTime)}
        </span>
        <span>
          {formatTime(futureTime)} {formatDate(futureTime)}
        </span>
      </div>
    </div>
  );
}
